import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import getNewsletterList from '@/js/helpers/newsletter-list'
import type { Newsletter } from '@/js/helpers/newsletter-list'
import { subscribeToNewsletters } from '@/js/helpers/newsletter-subscribe'
import { reset, validate } from '@/js/helpers/validate-fns'
import { useRogue } from '@/js/services/Rogue'
import { useCustomerStore } from '@/js/stores/customerStore'

// The status is undefined until a signup settles. 'blocked' means the captcha rejected it.
export type NewsletterSignupStatus = 'success' | 'error' | 'blocked'

// Template ref the form must put on its wrapper element
const ROOT_REF = 'root'

export type StatusMessage = {
  status: 'success' | 'error',
  icon?: string,
  text: string,
}

/*
 * Callers usually only want to change the copy, so `status` and `icon` fall back to the
 * defaults for that status - 'blocked' is why `status` exists at all, since it reads as an error.
 */
export type StatusMessageOverride = Partial<StatusMessage> & { text: string }

export const DEFAULT_STATUS_MESSAGES: Record<NewsletterSignupStatus, StatusMessage> = {
  success : { status: 'success', text: 'Your preferences have been saved.' },
  blocked : { status: 'error', icon: 'alert-error', text: 'We could not verify your request. Please reload the page and try again.' },
  error   : { status: 'error', icon: 'alert-error', text: 'There was an error saving your preferences.' },
}

export type UseNewsletterSignupOptions = {
  /** Supply the lists directly instead of deriving them from the site config */
  list?: Newsletter[],
  /** Use the descriptive suffix on the main newsletter labels */
  fullLabel?: boolean,
  /** Offer the Rogue Invitational lists alongside the main two */
  includeInvitational?: boolean,
  /** Newsletter names to pre-check */
  preselect?: string[],
  /** Prefill the email with the logged in customer's address */
  prefillEmail?: boolean,
  /** Scroll the first invalid field into view. Turn off for forms already in the viewport. */
  scrollToError?: boolean,
  /** Clear the form this many ms after a successful signup. 0 leaves it up to the caller. */
  resetAfterMs?: number,
  /** Also run that delayed reset when the signup fails - for forms the message replaces */
  resetAfterFailure?: boolean,
  /** Extra teardown to run whenever the form resets */
  onReset?: () => void,
  /** Override the default copy for any status */
  messages?: Partial<Record<NewsletterSignupStatus, StatusMessageOverride>>,
}

/*
 * Shared state and submit handling for the newsletter signup forms (footer, login, deals).
 *
 * The form must put ref="root" on its wrapper element - that's what surfaces the inline
 * field errors and clears their touched state on reset.
 */
export function useNewsletterSignup(options: UseNewsletterSignupOptions = {}) {
  const rogue = useRogue()
  const customerStore = useCustomerStore()

  const {
    fullLabel = false,
    includeInvitational = false,
    preselect = [],
    prefillEmail = false,
    scrollToError = true,
    resetAfterMs = 0,
    resetAfterFailure = false,
  } = options

  function buildList(): Newsletter[] {
    const list = options.list
      ? options.list.map(newsletter => ({ ...newsletter }))
      : getNewsletterList(rogue, !includeInvitational, fullLabel)

    return list.map(newsletter => ({ ...newsletter, selected: preselect.includes(newsletter.name) }))
  }

  const root = useTemplateRef<HTMLElement>(ROOT_REF)
  const email = ref('')
  const status = ref<NewsletterSignupStatus>()
  const loading = ref(false)
  const attempted = ref(false)
  const newsletters = ref<Newsletter[]>(buildList())

  const selected = computed(() => newsletters.value.filter(newsletter => newsletter.selected).map(newsletter => newsletter.id))

  // Drives the "pick a list" hint, but only once they've tried to submit
  const noneSelected = computed(() => attempted.value && selected.value.length === 0)

  const statusMessage = computed<StatusMessage | undefined>(() => {
    if (!status.value) {
      return undefined
    }

    const fallback = DEFAULT_STATUS_MESSAGES[status.value]
    const override = options.messages?.[status.value]

    return override ? { ...fallback, ...override } : fallback
  })

  let resetTimer: ReturnType<typeof setTimeout> | undefined

  function clearResetTimer() {
    if (resetTimer) {
      clearTimeout(resetTimer)
      resetTimer = undefined
    }
  }

  function scheduleReset(succeeded: boolean) {
    if (resetAfterMs && (succeeded || resetAfterFailure)) {
      resetTimer = setTimeout(resetForm, resetAfterMs)
    }
  }

  function resetForm() {
    clearResetTimer()
    email.value = ''
    newsletters.value = buildList()
    attempted.value = false
    status.value = undefined

    if (root.value) {
      // Clears the inputs' touched state so errors don't flash on the emptied fields
      reset(root.value)
    }

    options.onReset?.()
  }

  async function subscribe(): Promise<boolean> {
    if (loading.value) {
      return false
    }

    attempted.value = true
    clearResetTimer()
    status.value = undefined

    // Surfaces the inline field errors rather than posting an invalid address
    if (root.value && !validate(root.value, scrollToError)) {
      return false
    }

    // noneSelected drives the messaging for this case
    if (!selected.value.length) {
      return false
    }

    loading.value = true

    try {
      const response = await subscribeToNewsletters(rogue, {
        email       : email.value,
        userId      : customerStore._id,
        newsletters : selected.value,
      })

      status.value = response.success ? 'success' : 'error'
      scheduleReset(response.success)

      return response.success
    } catch (e) {
      const error = e as { status?: number, message?: string }

      // 403 means the captcha rejected us - a retry needs a fresh token, so ask for a reload
      status.value = error.status === 403 ? 'blocked' : 'error'
      rogue.log.warn(`Newsletter signup failed (${error.status || 'unknown'}): ${error.message || ''}`)
      scheduleReset(false)

      return false
    } finally {
      loading.value = false
    }
  }

  onMounted(() => {
    if (prefillEmail) {
      email.value = customerStore.email || ''
    }
  })

  onBeforeUnmount(clearResetTimer)

  // `root` is deliberately not returned - useTemplateRef resolves ref="root" through the
  // component instance, and exposing it as a setup binding would break that wiring
  return {
    email,
    status,
    loading,
    newsletters,
    selected,
    noneSelected,
    statusMessage,
    subscribe,
  }
}
